import { ES } from './es';

/**
 * The translation catalog's shape, DERIVED from the Spanish source.
 *
 * `es.ts` is the single place a key is ever written. This file used to
 * restate every key twice more — once in a hand-maintained `TRANSLATION`
 * type and once in a hand-maintained `i18n` object of dotted paths —
 * which made adding one key a three-file edit and let the copies drift.
 * They had: when this derivation replaced them, the hand-written `i18n`
 * was missing 336 keys that `es.ts` already defined (232 under
 * `PERMISSIONS`, 62 under `HISTORY_FIELDS`, 42 under
 * `HISTORY_ITEM_FIELDS`). No key was ever missing in the other
 * direction — the generated object is a strict superset.
 *
 * `ES` is deliberately NOT declared `as const`: literal types would make
 * `i18n.REPORTS.TOTAL_PAGE` typed as the Spanish text `'Total página'`
 * while actually holding the path `'REPORTS.TOTAL_PAGE'`. A plain object
 * literal widens the leaves to `string`, which is what both the type and
 * the runtime value need.
 *
 * A second locale still typechecks against this: `const EN: TRANSLATION
 * = { … }` forces it to cover exactly the same shape.
 */
export type TRANSLATION = typeof ES;

/**
 * Walk the catalog and replace every leaf with its own dotted path, so
 * `i18n.REPORTS.TOTAL_PAGE === 'REPORTS.TOTAL_PAGE'` — the key i18next
 * resolves, never typed by hand.
 *
 * Arrays are rebuilt as ARRAYS of indexed paths (`X.ITEMS.0`,
 * `X.ITEMS.1`, …). Recursing into them with the object branch would
 * return `{ 0: …, 1: … }`, which satisfies no caller that maps over the
 * value — `es.ts` has tuple leaves (e.g. `CONFIGURATION.MODULES.*.ITEMS`)
 * that are rendered as lists.
 */
function createKeys<T>(obj: T, prefix = ''): T {
  if (Array.isArray(obj)) {
    return obj.map((_, index) =>
      prefix ? `${prefix}.${index}` : String(index)
    ) as unknown as T;
  }

  const result = {} as Record<string, unknown>;
  for (const key of Object.keys(obj as Record<string, unknown>)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    const value = (obj as Record<string, unknown>)[key];
    result[key] =
      typeof value === 'object' && value !== null
        ? createKeys(value, fullKey)
        : fullKey;
  }
  return result as T;
}

/**
 * Type-safe translation keys. Use as `t(i18n.COMMON.SAVE)` — never a
 * string literal, which loses the compile-time check.
 */
export const i18n = createKeys(ES);
